import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { Card, Segmented, Spinner, useToast } from '../components/ui';
import type { CookingGuidance, Language } from '../types';

const T = {
  en: {
    back: 'Back',
    ingredients: 'Ingredients',
    method: 'Method',
    serves: 'Serves',
    notes: 'Note',
    ask: 'Ask about this meal',
    askPlaceholder: 'e.g. Make this milder for the children',
    send: 'Ask',
    cooked: 'We cooked this',
    cookedDone: 'Saved',
    favourite: '♥ Family favourite',
    isFavourite: '♥ Family favourite ✓',
    favouriteTitle: 'That went down well — plan it more often',
    favouriteSaved: 'Added to family favourites',
    favouriteRemoved: 'Removed from family favourites',
    suggestions: [
      'Make this milder for the children',
      'Can I cook this in the oven?',
      "I don't have crème fraîche",
      'What can I prepare in advance?',
    ],
    fallback: 'Written from the meal structure — add an API key for tailored steps.',
  },
  fr: {
    back: 'Retour',
    ingredients: 'Ingrédients',
    method: 'Préparation',
    serves: 'Pour',
    notes: 'Note',
    ask: 'Une question sur ce plat',
    askPlaceholder: 'ex. Version plus douce pour les enfants',
    send: 'Demander',
    cooked: 'On l’a cuisiné',
    cookedDone: 'Enregistré',
    favourite: '♥ Favori de la famille',
    isFavourite: '♥ Favori de la famille ✓',
    favouriteTitle: 'Ça a plu — à programmer plus souvent',
    favouriteSaved: 'Ajouté aux favoris',
    favouriteRemoved: 'Retiré des favoris',
    suggestions: [
      'Version plus douce pour les enfants',
      'Puis-je le faire au four ?',
      "Je n'ai pas de crème fraîche",
      'Que puis-je préparer à l’avance ?',
    ],
    fallback: 'Étapes générées à partir de la structure du plat.',
  },
} as const;

/** The tablet-friendly cooking page (§49-53). */
export function CookPage({
  language,
  setLanguage,
}: {
  language: Language;
  setLanguage: (language: Language) => void;
}) {
  const { plannedMealId = '' } = useParams();
  const t = T[language];
  const toast = useToast();

  const [guidance, setGuidance] = useState<CookingGuidance | null>(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [cooked, setCooked] = useState(false);
  const [favourite, setFavourite] = useState(false);
  // Reflect what is actually stored, so the button is not a lie on arrival.
  useEffect(() => {
    if (guidance) setFavourite(Boolean(guidance.isFavourite));
  }, [guidance]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGuidance(await api.cook(plannedMealId, language));
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not load the recipe');
    } finally {
      setLoading(false);
    }
  }, [plannedMealId, language, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const ask = async (text: string) => {
    if (!text.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const result = await api.ask(plannedMealId, text, language);
      setAnswer(result.answer);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not ask that');
    } finally {
      setAsking(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50vh' }}>
        <Spinner />
      </div>
    );
  }

  if (!guidance) return <p className="banner warn">Recipe unavailable.</p>;

  return (
    <div className="cook stack">
      <div className="row no-print">
        <Link className="btn ghost" to="/week">
          ← {t.back}
        </Link>
        <div className="spacer" />
        {/* The language toggle is always visible (§50). */}
        <Segmented
          value={language}
          ariaLabel="Language"
          options={[
            { value: 'en', label: 'English' },
            { value: 'fr', label: 'Français' },
          ]}
          onChange={(next) => setLanguage(next)}
        />
      </div>

      <div>
        <h1>{guidance.mealName}</h1>
        <p className="muted">
          {t.serves} {guidance.servesLabel} · {guidance.totalMinutes} min
        </p>
      </div>

      <Card title={t.ingredients}>
        <ul className="ingredients" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {guidance.ingredients.map((ingredient) => (
            <li key={`${ingredient.name}-${ingredient.quantity}`}>
              <span className="q">{ingredient.quantity}</span>
              <span>{ingredient.name}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title={t.method}>
        <ol className="steps">
          {guidance.steps.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ol>

        {guidance.notes && (
          <p className="banner" style={{ marginTop: 18 }}>
            <strong>{t.notes}:</strong> {guidance.notes}
          </p>
        )}
        {guidance.generatedBy === 'fallback' && (
          <p className="dim" style={{ marginTop: 14 }}>
            {t.fallback}
          </p>
        )}
      </Card>

      <Card title={t.ask}>
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          {t.suggestions.map((suggestion) => (
            <button key={suggestion} className="btn small" disabled={asking} onClick={() => void ask(suggestion)}>
              {suggestion}
            </button>
          ))}
        </div>

        <form
          className="row"
          style={{ gap: 8, flexWrap: 'nowrap' }}
          onSubmit={(event) => {
            event.preventDefault();
            void ask(question);
          }}
        >
          <input
            type="text"
            value={question}
            placeholder={t.askPlaceholder}
            onChange={(event) => setQuestion(event.target.value)}
          />
          <button className="btn primary" type="submit" disabled={asking || question.trim().length < 3}>
            {asking ? '…' : t.send}
          </button>
        </form>

        {answer && (
          <p style={{ marginTop: 16, whiteSpace: 'pre-wrap' }}>{answer}</p>
        )}
      </Card>

      <div className="row no-print" style={{ gap: 8, flexWrap: 'wrap' }}>
        {/* The moment you actually know whether it was any good. */}
        <button
          className={`btn${favourite ? ' on' : ''}`}
          title={t.favouriteTitle}
          aria-pressed={favourite}
          onClick={async () => {
            const next = !favourite;
            setFavourite(next);
            try {
              await api.favourite(guidance.mealId, next);
              toast(next ? t.favouriteSaved : t.favouriteRemoved);
            } catch (error) {
              setFavourite(!next);
              toast(error instanceof Error ? error.message : 'Could not save that');
            }
          }}
        >
          {favourite ? t.isFavourite : t.favourite}
        </button>
        <button
          className={`btn${cooked ? ' on' : ''}`}
          disabled={cooked}
          onClick={async () => {
            await api.markCooked(plannedMealId);
            setCooked(true);
            toast(t.cookedDone);
          }}
        >
          {cooked ? t.cookedDone : t.cooked}
        </button>
      </div>
    </div>
  );
}
